import { useCallback } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectItem,
  SelectTrigger,
  Text,
  TuneIcon,
  ViewListIcon,
} from '@billda/design-system';
import { useShallow } from 'zustand/react/shallow';
import { PageHeaderAction } from '../../../shared/components/layout/PageHeaderAction';
import {
  useDeductionsUIStore,
  type DeductionGroupingMode,
} from '../stores/deductionsUIStore';

/**
 * DeductionGroupingMenu — Linear-style "Display" popover that lets users
 * switch the deduction list between a recent-first flat list and grouping.
 * Reads and writes `groupingMode` from `deductionsUIStore`.
 */
export function DeductionGroupingMenu() {
  const { groupingMode, setGroupingMode } = useDeductionsUIStore(
    useShallow(state => ({
      groupingMode: state.groupingMode,
      setGroupingMode: state.setGroupingMode,
    }))
  );

  const handleGroupingChange = useCallback(
    (value: string) => {
      setGroupingMode(value as DeductionGroupingMode);
    },
    [setGroupingMode]
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <PageHeaderAction
          label="Display"
          icon={<TuneIcon />}
          data-testid="deduction-display-button"
        />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        aria-label="Deduction display options"
        className="min-w-70"
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <ViewListIcon
              aria-hidden="true"
              className="size-4 text-content-secondary"
            />
            <Text variant="body2" color="secondary">
              Grouping
            </Text>
          </div>
          <Select value={groupingMode} onValueChange={handleGroupingChange}>
            <SelectTrigger
              aria-label="Group deductions by"
              data-testid="grouping-select"
              className="min-w-35 bg-surface-raised text-[13px]"
            />
            <SelectItem value="none">None</SelectItem>
            <SelectItem value="worker">Worker / Gang</SelectItem>
            <SelectItem value="site">Site</SelectItem>
          </Select>
        </div>
      </PopoverContent>
    </Popover>
  );
}
